import './styles.scss';
import React, {Dispatch, useContext, useEffect, useRef, useState} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../../constants";

/* Bootstrap */
import {Button, Dropdown} from "react-bootstrap";
import Utils from "../../../utils";

/* Redux */
import {addToast} from "../../../redux/slices/toast-slice";
import {AnyAction} from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";

/* Modules */
import copy from 'copy-to-clipboard';
import DownloadService from "../../../api/download";
import {IViewerState} from "../../../types/redux/viewer";
import {RootState} from "../../../redux";
import {NavigateFunction} from "react-router/dist/lib/hooks";
import {useNavigate} from "react-router-dom";
import {searchReset, setSearch} from "../../../redux/slices/search-slice";
import SearchUtils from "../../../utils/search-utils";
import {IRecordsContext, RecordsContext, SelectedRecordsContext} from "../../../types/document";
import useError from "../../../hooks/useError";

import { saveAs } from "file-saver";

const ViewerCite = ():JSX.Element => {
  /* redux */
  const dispatch:Dispatch<AnyAction> = useDispatch();

  /* context */
  const selectedRecords: any[] = useContext<IRecordsContext>(SelectedRecordsContext).records; // bulk adding tag to selected records
  const records: any[] = useContext<IRecordsContext>(RecordsContext).records; // select from document card header
  const viewerRecord: any =  useSelector<RootState, IViewerState>(state => state.viewer).viewerRecord;

  /* state */
  const [citeStyle, setCiteStyle] = useState<string | null>(Constants.VANCOUVER);
  const [citeFileFormat, setCiteFileFormat] = useState<string | null>(null);
  const [recordsToCite, setRecordsToCite] = useState<any[]>([]);

  /* refs */
  const textFieldRef = useRef(null);

  /* hooks */
  const navigate:NavigateFunction = useNavigate();
  const {handleError} = useError();

  useEffect(()=>{
    const _records: any[] = (selectedRecords?.length > 0) ? selectedRecords : (records.length > 0) ? records : [viewerRecord];
    setRecordsToCite(_records ? _records : [])
  }, [selectedRecords, records, viewerRecord])

  const journals = (record: any): JSX.Element | null =>{
    const handleClick = (link: string)=>{
      dispatch(searchReset())
      dispatch(setSearch(SearchUtils.fromDataSource(link.split('?')[1])));
      setTimeout(() => {
        navigate(link);
      })
    }

    if (recordsToCite.length === 0) return;

    const journalFields: any[] | null = record.journal ? JSON.parse(record.journal): null;
    if (journalFields && (journalFields.length > 0) && record) {
      return (<>
        <p><strong>Papers citing this document {(journalFields.length > 1) ? '(' + journalFields.length + '):' : ':'}</strong></p>
        {journalFields.map((j:any, index:number) => {
          const link: string = '/' + record.industry.toLowerCase() + '/' + Constants.BIBLIOGRAPHY + '/?q=null,all,contains,refid:' + j.id + '&db-set=bibliography&pg=1'
          return (<p key={'cited_' + index}>
            {<Button className={'btn-link text-start p-0'} onClick={()=>handleClick(link)}><span className={'max-lines lines-2 text-small text-start'}>{j.title}</span></Button>}
            <p className={'mt-1'}>{j.author}</p>
          </p>)
         })}
      </>)
    }
    return null;
  }

  const handleCopy = ()=> {
    let html: any = textFieldRef.current;
    if (html) {
      const htmlStr: string = html.innerHTML;
      const contentString: string = Utils.removeTags(htmlStr.split('</div>').join('\n'));
      try {
        if (copy(contentString)) {
          dispatch(addToast({
            bg: 'bg-success',
            msg: Constants.CITE_COPIED,
            id: -1
          }))
        } else {
          // todo: jain - handle error
          console.log('Unable to copy to clipboard')
        }
      } catch (e) {
        throw new Error('Unable to copy to clipboard')
      }
      copy(contentString)
    }
  }

  const handleBlob = (response: any, filename:string, contentType:any)=>{
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch: any = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create a Blob from the response data
    const blob:Blob = new Blob([response.data], { type: contentType });

    // Use file-saver to initiate the download
    saveAs(blob, filename);
  }

  const handleDownload = ()=>{
    let format: string = 'txt';
    let ext: string = 'txt';
    let contentType: string = 'data:text/plain;charset=utf-8;';

    switch (citeFileFormat) {
      case Constants.REFWORKS:
        format = 'refworks';
        break;

      case Constants.ENDNOTE:
        format = 'endnote';
        break;

      case Constants.EXCEL:
        format = 'excel';
        ext = 'xls';
        contentType = 'application/vnd.ms-excel'
        break;

      case Constants.CSV:
        format = 'csv';
        ext = 'csv';
        break;
    }

    DownloadService.downloadCite(format, recordsToCite.map(r => r.id)).then(response=>{
      handleBlob(response, 'idl_cite.' + ext, contentType)
    }).catch(e => handleError(e))
  }

  const cite = (record: any): JSX.Element => {
    const url: string = window.location.protocol + '//' + window.location.host + '/docs/' +record.id +'/';
    let source: any = record.source;
    try {
       source = JSON.parse(record.source).map((s:any) => s.title).join(';');
    } catch {}

    switch (citeStyle) {
      case Constants.VANCOUVER:
        return <>
          {record.au && <span>{record.au}. </span>}
          <span>{record.ti}. </span>
          <span>UCSF Industry Documents Library, </span>
          {record.collection && <span>{record.collection.join(', ')}, </span>}
          <span>{source} </span>
          <span>{record.dd} [cited: {new Date().toLocaleString('default', {month: 'short', day: 'numeric', year:'numeric'})} ] </span>
          <span>Available: {url}.</span>
        </>

      case Constants.AMA:
        return <>
          {record.au && <span>{record.au}. </span>}
          <span>{record.ti}. </span>
          <span>UCSF Industry Documents Library, </span>
          {record.collection && <span>{record.collection.join(', ')}, </span>}
          <span>{source} </span>
          <span>{record.dd}. Accessed: {new Date().toLocaleString('default', {month: 'short', day: 'numeric', year:'numeric'})}. </span>
          <span>{url}.</span>
        </>

      case Constants.APA:
        return <>
          {record.au && <span>{record.au}. </span>}
          <span>({record.dd}). </span>
          <span><i>{record.ti}</i>. </span>
          <span>UCSF Industry Documents Library, </span>
          {record.collection && <span>{record.collection.join(', ')}, </span>}
          {source && <span>{source}. </span>}
          <span>{url}.</span>
        </>

      case Constants.MLA:
        return <>
          {record.au && <span>{record.au}. </span>}
          <span>“{record.ti}”. </span>
          <span>{record.dd}. </span>

          <span><i>UCSF Industry Documents Library, </i></span>
          {record.collection && <span><i>{record.collection.join(', ')}, </i></span>}
          {source && <span><i>{source}</i>. </span>}
          <span>{url}.</span>
          <span>Accessed {new Date().toLocaleString('default', {month: 'short', day: 'numeric', year:'numeric'})}. </span>
        </>
    }

    return <></>
  }

  return  (
    (recordsToCite.length > 0) ? <div className={'viewer-cite px-3'}>
      <div className={'cite-container mt-4 px-2 py-2 mb-3 border rounded'} ref={textFieldRef}>
        {recordsToCite.map(r=>{
          return <div className={'cite'}>{cite(r)}</div>
        })}
       </div>

      <Dropdown
        className={'cite-format-dropdown pt-2'}
        onSelect={(eventKey: string | null)=>setCiteStyle(eventKey)}
      >
        <Dropdown.Toggle
          variant={'light'}
          id={'select-cita'}
          className={'mt-1 w-100 text-center bg-white border border-dark py-2 text-xnormal'}
          value={citeStyle ? citeStyle : ''}
        >
          <span className={'ms-2 me-1'}>{citeStyle ? citeStyle : 'Select Citation Style'}</span>
        </Dropdown.Toggle>

        <Dropdown.Menu className={'w-100 text-xnormal'}>
          <Dropdown.Item href={''} eventKey={Constants.VANCOUVER}>{Constants.VANCOUVER}</Dropdown.Item>
          <Dropdown.Item href={''} eventKey={Constants.AMA}>{Constants.AMA}</Dropdown.Item>
          <Dropdown.Item href={''} eventKey={Constants.APA}>{Constants.APA}</Dropdown.Item>
          <Dropdown.Item href={''} eventKey={Constants.MLA}>{Constants.MLA}</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <Button
        className={'w-100 bg-transparent border border-dark mt-2 btn-hover-grey-md'}
        onClick={()=>handleCopy()}
      >
        <i className={'me-2 bi bi-clipboard-plus text-body'}/>
        <span className={'text-body border-dark'}>Copy</span>
      </Button>

      <Dropdown
        className={'download-dropdown pt-4'}
        onSelect={(eventKey: string | null)=>setCiteFileFormat(eventKey)}
      >
        <Dropdown.Toggle
          variant={'light'}
          id={'select-cita'}
          className={'w-100 text-start bg-transparent border border-dark py-2 text-xnormal'}
          value={citeFileFormat ? citeFileFormat : ''}
        >
          <span className={'ms-2 me-1'}>{citeFileFormat ? Utils.toTitleCase(citeFileFormat.replace(/-/g, ' ')) : 'Select Download Format'}</span>
        </Dropdown.Toggle>

        <Dropdown.Menu className={'w-100 border-dark'}>
          <Dropdown.Item href={'#'} eventKey={Constants.ENDNOTE}>{Utils.toTitleCase(Constants.ENDNOTE)}</Dropdown.Item>
          <Dropdown.Item href={'#'} eventKey={Constants.REFWORKS}>{Utils.toTitleCase(Constants.REFWORKS)}</Dropdown.Item>
          <Dropdown.Item href={'#'} eventKey={Constants.EXCEL}>{Utils.toTitleCase(Constants.EXCEL)}</Dropdown.Item>
          <Dropdown.Item href={'#'} eventKey={Constants.CSV}>{Constants.CSV.toUpperCase()}</Dropdown.Item>
          <Dropdown.Item href={'#'} eventKey={Constants.PLAIN_TEXT}>{Utils.toTitleCase(Constants.PLAIN_TEXT)}</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <Button
        className={'w-100 bg-transparent border-dark border btn-hover-grey-md mt-2'}
        onClick={()=>handleDownload()}
      >
        <i className={'me-2 bi bi-download text-body'}/>
        <span className={'text-body ext-xnormal'}>Download</span>
      </Button>

      <div className={'mt-3 text-smaller'}>
        {recordsToCite.map((r,i)=>{
          return <div key={i}>{journals(r)}</div>
        })}
      </div>
    </div>
   : <></>
  )
}
export default ViewerCite;
